import { useState, useCallback, useEffect } from 'react';
import { auth } from '../firebase/firebase';
import { createUserWithEmailAndPassword, sendEmailVerification, signOut as firebaseSignOut } from 'firebase/auth';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../api/api';
import { TosContent } from './TosContent';
import { ReactComponent as Logo } from '../censuszen-logo.svg';
import '../styles/Auth.css';

const FACILITY_TYPES = [
    { value: 'IPR', label: 'Inpatient Rehabilitation (IPR)' },
    { value: 'LTACH', label: 'Long-Term Acute Care (LTACH)' },
    { value: 'Home Health', label: 'Home Health' },
    { value: 'Hospice', label: 'Hospice' },
    { value: 'Other', label: 'Other' },
];

export const Register = () => {
    const [searchParams] = useSearchParams();
    const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [facilityType, setFacilityType] = useState('');
    const [facilityTypeOther, setFacilityTypeOther] = useState('');
    const [tosAccepted, setTosAccepted] = useState(false);
    const [tosRead, setTosRead] = useState(false);
    const [showTosModal, setShowTosModal] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [inviteValid, setInviteValid] = useState(null);
    const [inviteChecking, setInviteChecking] = useState(false);
    const [inviteFacility, setInviteFacility] = useState(null); // { id, name } or null
    const navigate = useNavigate();

    const validateInvite = useCallback(async (code) => {
        const codeToCheck = code || inviteCode;
        if (!codeToCheck.trim()) return;
        setInviteChecking(true);
        setError('');
        try {
            const resp = await api.post('/user/validate-invite', { invite_code: codeToCheck.trim() });
            if (resp.data.valid) {
                setInviteValid(true);
                if (resp.data.facility_id && resp.data.facility_name) {
                    setInviteFacility({ id: resp.data.facility_id, name: resp.data.facility_name });
                } else {
                    setInviteFacility(null);
                }
            } else {
                setInviteValid(false);
                setInviteFacility(null);
                setError('Invalid or already used invite code.');
            }
        } catch (err) {
            setError('Failed to validate invite code.');
            setInviteValid(false);
            setInviteFacility(null);
        } finally {
            setInviteChecking(false);
        }
    }, [inviteCode]);

    useEffect(() => {
        const codeFromUrl = searchParams.get('code');
        if (codeFromUrl) {
            validateInvite(codeFromUrl);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTosScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 20) {
            setTosRead(true);
        }
    }, []);

    const handleCloseTosModal = () => {
        setShowTosModal(false);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }

        if (!tosAccepted) {
            setError('You must accept the Terms of Service.');
            return;
        }

        if (!facilityType) {
            setError('Please select your facility type.');
            return;
        }

        if (!inviteValid) {
            setError('Please validate your invite code first.');
            return;
        }

        setLoading(true);
        try {
            // 1. Create Firebase account
            const cred = await createUserWithEmailAndPassword(auth, email, password);

            // 2. Send verification email
            await sendEmailVerification(cred.user);

            // 3. Create backend profile (uses the new user's auth token)
            const profilePayload = {
                name: name.trim(),
                email: email.trim(),
                invite_code: inviteCode.trim(),
                facility_type: facilityType,
            };
            if (facilityType === 'Other' && facilityTypeOther.trim()) {
                profilePayload.facility_type_other = facilityTypeOther.trim();
            }
            await api.post('/user/profile', profilePayload);

            // 4. Accept ToS
            await api.post('/user/accept-tos');

            // 5. Sign out and redirect to login with success message
            await firebaseSignOut(auth);
            navigate('/login', {
                state: { message: 'Account created! Please check your email to verify, then log in.' }
            });
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Registration failed.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container auth-container--wide">
                <Link to="/" className="auth-logo-link">
                    <Logo className="auth-logo" aria-label="CensusZen" />
                </Link>
                <form onSubmit={handleRegister} className="auth-form">
                    <h2 className="auth-title">Create your account</h2>
                    <p className="auth-subtitle">Get started with CensusZen</p>
                    {error && <div className="auth-error">{error}</div>}

                    <div className="auth-field">
                        <label htmlFor="inviteCode">Invite Code</label>
                        <div className="auth-invite-row">
                            <input
                                id="inviteCode"
                                type="text"
                                value={inviteCode}
                                onChange={(e) => { setInviteCode(e.target.value); setInviteValid(null); setInviteFacility(null); }}
                                required
                                placeholder="Enter your invite code"
                            />
                            <button
                                type="button"
                                onClick={() => validateInvite()}
                                disabled={inviteChecking || !inviteCode.trim()}
                                className="auth-validate-btn"
                            >
                                {inviteChecking ? 'Checking...' : 'Validate'}
                            </button>
                        </div>
                        {inviteValid === true && (
                            <>
                                <span className="auth-field-success">Invite code valid</span>
                                {inviteFacility && (
                                    <div className="auth-facility-badge">
                                        You are joining <strong>{inviteFacility.name}</strong>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="auth-field">
                        <label htmlFor="name">Full Name</label>
                        <input
                            id="name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            autoComplete="name"
                            placeholder="Jane Smith"
                        />
                    </div>

                    <div className="auth-field">
                        <label htmlFor="regEmail">Email</label>
                        <input
                            id="regEmail"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="username"
                            placeholder="you@example.com"
                        />
                    </div>

                    <div className="auth-field">
                        <label htmlFor="facilityType">Facility Type</label>
                        <select
                            id="facilityType"
                            value={facilityType}
                            onChange={(e) => setFacilityType(e.target.value)}
                            required
                        >
                            <option value="" disabled>Select your facility type</option>
                            {FACILITY_TYPES.map((ft) => (
                                <option key={ft.value} value={ft.value}>
                                    {ft.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {facilityType === 'Other' && (
                        <div className="auth-field">
                            <label htmlFor="facilityTypeOther">Please specify</label>
                            <input
                                id="facilityTypeOther"
                                type="text"
                                value={facilityTypeOther}
                                onChange={(e) => setFacilityTypeOther(e.target.value)}
                                placeholder="e.g. Skilled Nursing Facility"
                            />
                        </div>
                    )}

                    <div className="auth-field">
                        <label htmlFor="regPassword">Password</label>
                        <input
                            id="regPassword"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            autoComplete="new-password"
                            placeholder="At least 6 characters"
                        />
                    </div>

                    <div className="auth-field">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                            placeholder="Confirm your password"
                        />
                    </div>

                    <div className="auth-tos-checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={tosAccepted}
                                onChange={(e) => setTosAccepted(e.target.checked)}
                                disabled={!tosRead}
                            />
                            {' '}I agree to the{' '}
                            <button
                                type="button"
                                className="auth-tos-link"
                                onClick={() => setShowTosModal(true)}
                            >
                                Terms of Service
                            </button>
                            {' '}and{' '}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="auth-tos-link">
                                Privacy Policy
                            </a>
                        </label>
                    </div>

                    <button type="submit" className="auth-submit" disabled={loading || !inviteValid || !facilityType}>
                        {loading ? 'Creating Account...' : 'Create Account'}
                    </button>

                    <div className="auth-links">
                        <Link to="/login">Already have an account? Sign in</Link>
                        <Link to="/request-access">Don't have an invite code? Request access</Link>
                    </div>
                </form>
            </div>

            {showTosModal && (
                <div className="tos-modal-overlay" onClick={() => tosRead && handleCloseTosModal()}>
                    <div className="tos-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Terms of Service</h2>
                        <div className="tos-modal-content" onScroll={handleTosScroll}>
                            <TosContent />
                        </div>
                        <button
                            type="button"
                            className="tos-modal-close"
                            disabled={!tosRead}
                            onClick={handleCloseTosModal}
                        >
                            {tosRead
                                ? 'I have reviewed the Terms of Service'
                                : 'Please scroll to read the full Terms of Service'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
